import { MapboxStyleSwitcherControl } from "mapbox-gl-style-switcher";
import "mapbox-gl-style-switcher/styles.css";
import ReactDOMServer from "react-dom/server";
import Popup from "./Popup";

const styles = [
  {
    title: "Terrain",
    uri: "mapbox://styles/polargeospatial/ckjlsqfwd0v1719ohfubbn786",
  },
  {
    title: "Satellite",
    uri: "mapbox://styles/polargeospatial/ckjluxjcs19qm19panzpmbe1k",
  },
];

const satcolors = [125, 200];
const terraincolors = [125, 100];

const toggleOffsetAlert = (zoom, styleName, setOffsetAlert) => {
  zoom >= 14 && styleName.includes("TreesSat")
    ? setOffsetAlert(true)
    : setOffsetAlert(false);
};

export const addLayers = (map, colors) => {
  if (map.getLayer("treesPoly")) {
    map.removeSource("treesPoly");
    map.removeLayer("treesPoly");
  }
  if (map.getLayer("treesPts")) {
    map.removeSource("treesPts");
    map.removeLayer("treesPts");
  }

  map.addSource("treeRaster", {
    type: "raster",
    tiles: [
      "https://trees.pgc.umn.edu/services/coverage_density_0_13z/tiles/{z}/{x}/{y}.png",
    ],
    maxzoom: 12,
  });
  map.addLayer(
    {
      id: "treesRaster",
      type: "raster",
      source: "treeRaster",
      paint: {
        "raster-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.9, 15, 0],
        // "raster-resampling": "nearest",
      },
    },
    "water"
  );

  map.addSource("treesPoly", {
    type: "vector",
    tiles: [
      "https://trees.pgc.umn.edu/services/4_tilejoin_poly_all16/tiles/{z}/{x}/{y}.pbf",
    ],
    minzoom: 11,
    maxzoom: 16,
  });
  map.addLayer(
    {
      id: "treesPoly",
      type: "fill",
      source: "treesPoly",
      "source-layer": "treePoly",
      layout: {},
      paint: {
        "fill-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0, 12, 1],
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "hsl(" + colors[0] + ", 96%, 71%)",
          "hsl(" + colors[0] + ", 96%, 31%)",
        ],
      },
    },
    "water"
  );

  map.addLayer({
    id: "cutlineHighlight",
    type: "fill",
    source: "composite",
    "source-layer": "cutlines",
    layout: {},
    paint: {
      "fill-outline-color": "#484896",
      "fill-color": "rgba(255,255,255,0)",
    },
    filter: ["in", "CAT_ID", ""],
  });
};

function addSpaceToScaleBar() {
  // Tucker wants a space between the number and the unit and mapbox's solution is not consistent (&nbsp; is stripped sometimes...)
  const element = document.querySelector(".mapboxgl-ctrl-scale");
  const textContent = element.textContent.trim();
  const [number, unit] = textContent.match(/\d+|[^\d]+/g);
  const span = document.createElement("span");
  span.textContent = number + " " + unit;
  element.innerHTML = "";
  element.appendChild(span);
}

export function initializeMap(
  mapboxgl,
  map,
  setOffsetAlert,
  setNoDataAlert,
  setAerosolAlert,
  setCloudAlert
) {
  map.on("style.load", function () {
    let styleName = map.getStyle().name;
    if (styleName.includes("TreesSat")) {
      toggleOffsetAlert(map.getZoom(), styleName, setOffsetAlert);
      addLayers(map, satcolors);
    } else {
      addLayers(map, terraincolors);
    }
  });

  map.on("load", function () {
    map.addControl(new MapboxStyleSwitcherControl(styles, "Terrain"));
    map.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
    addSpaceToScaleBar();
    map.on("zoom", () => {
      addSpaceToScaleBar();
    });
    map.on("moveend", function () {
      let zoom = map.getZoom();
      updateURL(zoom, map.getCenter());
      toggleOffsetAlert(zoom, map.getStyle().name, setOffsetAlert);
    });

    map.on("mouseenter", "treesPoly", (e) => {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "treesPoly", function () {
      map.getCanvas().style.cursor = "";
    });

    const setAlert = (state, setter) => {
      setter(state);
      state
        ? (map.getCanvas().style.cursor = "context-menu")
        : (map.getCanvas().style.cursor = "");
    };

    map.on("mouseenter", "nodatasimp-3z1yjl", () => {
      setAlert(true, setNoDataAlert);
    });
    map.on("mouseleave", "nodatasimp-3z1yjl", () => {
      setAlert(false, setNoDataAlert);
    });
    map.on("mouseenter", "cloud-cover-dissolved-simp", () => {
      setAlert(true, setCloudAlert);
    });
    map.on("mouseleave", "cloud-cover-dissolved-simp", () => {
      setAlert(false, setCloudAlert);
    });
    map.on("mouseenter", "aerosol-dissolved-simp", () => {
      setAlert(true, setAerosolAlert);
    });
    map.on("mouseleave", "aerosol-dissolved-simp", () => {
      setAlert(false, setAerosolAlert);
    });

    const popup = new mapboxgl.Popup({
      offset: 20,
      className: "custom-gl-popup",
    });

    const setPopupClose = (id = 0) => {
      popup.on("close", () => {
        map.setFilter("cutlineHighlight", ["in", "CAT_ID", ""]);
        if (id !== 0) {
          map.setFeatureState(
            {
              source: "treesPoly",
              sourceLayer: "treePoly",
              id: id,
            },
            {
              selected: false,
            }
          );
        }
      });
    };

    map.on("click", (e) => {
      popup.remove();
      const coordinates = [e.lngLat.lng, e.lngLat.lat];
      const bbox = [
        [e.point.x - 1, e.point.y - 1],
        [e.point.x + 1, e.point.y + 1],
      ];
      const featuresArr = map.queryRenderedFeatures(bbox, {
        layers: ["treesPoly", "cutlines"],
      });
      const cutlines = featuresArr.filter((feature) => {
        return feature.sourceLayer === "cutlines";
      });
      const trees = featuresArr.filter((feature) => {
        return feature.sourceLayer === "treePoly";
      });

      if (cutlines.length) {
        map.setFilter("cutlineHighlight", [
          "in",
          "CAT_ID",
          cutlines[0].properties.CAT_ID,
        ]);
        setPopupClose();
      }
      if (trees.length) {
        map.setFeatureState(
          {
            source: "treesPoly",
            sourceLayer: "treePoly",
            id: trees[0].id,
          },
          {
            selected: true,
          }
        );
        setPopupClose(trees[0].id);
      }

      const description = ReactDOMServer.renderToStaticMarkup(
        trees.length || cutlines.length ? (
          <Popup
            trees={trees}
            cutlines={cutlines}
            coords={[e.lngLat.lng, e.lngLat.lat]}
          />
        ) : null
      );
      if (trees.length || cutlines.length) {
        popup.setLngLat(coordinates).setHTML(description).addTo(map);
      }
    });

    const updateURL = (zoom, center) => {
      var getUrl = window.location;
      var baseUrl = getUrl.protocol + "//" + getUrl.host + "/";
      const { lng, lat } = center;
      window.history.replaceState(
        null,
        null,
        baseUrl +
          "app?zoom=" +
          zoom.toFixed(5) +
          "&lng=" +
          lng.toFixed(5) +
          "&lat=" +
          lat.toFixed(5)
      );
    };
  });
}
